<template>
  <div
    class="stat_item"
    @click.stop="
      handleClick({
        ...stat,
        calcVal: $filters.getStatsValue(stat.key, value ?? 0),
      })
    "
    :style="{ borderColor: color }"
    :class="{
      shadeTwo: predefined || colorFade,
      compact: predefined,
      nonValue: value == 0,
      selectedOutline: stat.isSelected && hideTick,
      isLink: ['followersCount', 'followingCount'].includes(stat.key),
      disabled: disabled,
    }"
    v-if="value > 0 || allowZero"
  >
    <IconComponent
      icon="minus.svg"
      class="topRightIcon c-pointer"
      @click.stop="onRemove"
      @touchstart="onRemove"
      v-if="showRemove"
    />
    <IconComponent
      class="topRightIcon"
      icon="icon-tick-info-dv-two.svg"
      v-if="showSelected && stat.isSelected && !hideTick"
      noRtlTransform
    />
    <div class="stat_item-text">
      <template v-if="stat.label === 'Interests'">
        <h6 ref="label">{{ $t("common.common.attributes") }}</h6>
      </template>
      <template v-else-if="stat.label === 'Favorite Investments'">
        <el-tooltip
          v-if="isOverflowing"
          class="box-item"
          effect="dark"
          :content="'Favorite Symbols'"
          placement="top-start"
        >
          <h6 ref="label">{{ $t("common.common.favorite_symbols") }}</h6>
        </el-tooltip>
        <h6 ref="label" v-else>{{ $t("common.common.favorite_symbols") }}</h6>
      </template>
      <template v-else>
        <el-tooltip
          v-if="isOverflowing"
          class="box-item"
          effect="dark"
          :content="stat.label"
        >
          <h6 ref="label">{{ $t(`profile.stats.${stat.key}`) }}</h6>
        </el-tooltip>
        <h6 ref="label" v-else>{{ $t(`profile.stats.${stat.key}`) }}</h6>
      </template>
      <p
        v-if="stat.key === 'investments'"
        v-html="
          formatWithComma(
            $filters.systemNumberConvention(
              locale === 'ar' && value % 1 !== 0 ? value.toFixed(7) : value,
              true,
              false,
              100
            )
          )
        "
      ></p>
      <p
        v-else-if="
          stat.label === 'Avg TR/Month' || stat.label === 'Avg TR/Week'
        "
        v-html="locale === 'ar' && value % 1 !== 0 ? value.toFixed(7) : value"
      ></p>
      <p
        v-else
        v-html="
          locale === 'ar' && value % 1 !== 0
            ? formatWithComma(value.toFixed(7))
            : formatWithComma(value)
        "
      ></p>
    </div>

    <div class="stat_item-icon" v-if="icon && showIcon" ref="statIconDv">
      <IconComponent :icon="icon + '.svg'" path="/stats-icon" noRtlTransform />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  watch,
  nextTick,
  onBeforeUnmount,
} from "vue";
import { useI18n } from "vue-i18n";
import useEmitter from "@/services/broadcast.service";
import storageService from "@/services/storage.service";
import utilService from "@/services/util.service";
import { useProfileScreenComposable } from "../../composables/profile.screen.composable";

const STAT_CONFIG: Record<string, { color: string; icon: string }> = {
  followersCount: { color: "#ff851b", icon: "icon-followers" },
  followingCount: { color: "#21c1fd", icon: "icon-following" },
  friendCount: { color: "#cbff18", icon: "icon-friends" },
  investmentsCount: { color: "#598aff", icon: "icon-investment" },
  investments: { color: "#ff6ea3", icon: "icon-price" },
  trades: { color: "#fcf6cb", icon: "icon-trade" },
  tradesPrice: { color: "#DCBEFF", icon: "icon-trades-dollor" },
  avgGainPrice: { color: "#39d3d3", icon: "icon-price" },
  avgGain: { color: "#34E36F", icon: "icon-count" },
  topGainPrice: { color: "#6FFCD0", icon: "icon-top-gain-dollor" },
  topGain: { color: "#f2aa1f", icon: "icon-count" },
  avgTradesPerMonth: { color: "#34E36F", icon: "icon-monthly-trade" },
  avgTradesPerWeek: { color: "#934BFF", icon: "icon-week-trade" },
  viewsCount: { color: "#00bcd4", icon: "icon-view" },
  outreach: { color: "#009e3a", icon: "icon-outreach" },
  tradesThisMonth: { color: "#34E36F", icon: "icon-monthly-trade" },
  tradesThisWeek: { color: "#934BFF", icon: "icon-week-trade" },
  tradesThisYear: { color: "#8EC001", icon: "icon-yearly-trades" },
  todayTrades: { color: "#6600ff", icon: "icon-calendar" },
  traderTypeCount: { color: "#F2AB3C", icon: "icon-trader-type" },
  bestTradeCount: { color: "#fba1a1", icon: "icon-best-trade" },
  favoritePlatformCount: { color: "#D33F6F", icon: "icon-favorite-platform" },
  recommendationCount: { color: "#598AFF", icon: "icon-recommendation" },
  watchListCount: { color: "#85A335", icon: "icon-watchlist" },
  listCount: { color: "#ccc", icon: "icon-list" },
  linksCount: { color: "#EB4F41", icon: "icon-link" },
  imagesCount: { color: "#ffff00", icon: "icon-image" },
  favoriteInfluencerCount: {
    color: "#8B4AF1",
    icon: "icon-favorite-influencers",
  },
  favoriteInvestmentCount: {
    color: "#EE8131",
    icon: "icon-favorite-investment",
  },
  playbookCount: { color: "#E9E582", icon: "icon-playbook" },
  profitAndLossCount: { color: "#73E5EB", icon: "icon-profit-loss" },
  predictionCount: { color: "#F89272", icon: "icon-prediction" },
  socialLnksCount: { color: "#EB4F41", icon: "icon-link" },
};

export default defineComponent({
  props: {
    showIcon: { type: Boolean, default: true },
    stat: Object,
    showSelected: Boolean,
    userCounts: Object,
    fromPopup: Boolean,
    showRemove: Boolean,
    userData: Object,
    colorFade: Boolean,
    isEditableView: { type: Boolean, default: false },
    hideTick: { type: Boolean, default: false },
    predefined: Boolean,
    disabled: Boolean,
    fromOutside: Boolean,
    /** Show card even when computed value is 0 (e.g. post section picker) */
    allowZero: { type: Boolean, default: false },
  },
  setup(props, context) {
    const { locale } = useI18n();
    const { handleStatsClick } = useProfileScreenComposable();
    const emitter = useEmitter();

    const statIconDv = ref<HTMLElement | null>(null);
    const value = ref<number>(0);
    const icon = ref<string>("icon-count");
    const color = ref<string>("#598aff");
    const user = ref<any>(storageService.getUser());
    const label = ref<HTMLElement | null>(null);
    const isOverflowing = ref(false);

    const checkOverflow = () => {
      nextTick(() => {
        if (label.value) {
          isOverflowing.value =
            label.value.scrollWidth > label.value.clientWidth;
        }
      });
    };

    const onRemove = () => context.emit("onRemove", true);

    const rootKeys = [
      "followersCount",
      "followingCount",
      "friendCount",
      "viewsCount",
      "outreach",
    ];

    const getPrivateValue = (key: string) => {
      if (rootKeys.includes(key)) {
        return props.userData?.[key] ?? 0;
      } else {
        return props.userData?.stats?.[key] ?? 0;
      }
    };

    const setStatsValue = () => {
      // Map keys to props.userCounts
      const mapCounts: Record<string, string> = {
        followersCount: "followersCount",
        followingCount: "followingCount",
        friendCount: "friendsCount",
      };

      // Always override these 3 stats using userCounts
      if (mapCounts[props.stat.key] && props.userCounts) {
        value.value = props.userCounts?.[mapCounts[props.stat.key]] ?? 0;
        return;
      }

      // Default: private values from userData
      value.value = getPrivateValue(props.stat.key);
    };

    const mapValueForStat = (stat: any) => {
      if (!stat.key) stat.key = utilService.getStatKey(stat.label);
      stat.label = utilService.getStatLabel(stat);

      const config = STAT_CONFIG[stat.key];
      if (config) {
        color.value = config.color;
        icon.value = config.icon;
      }

      setTimeout(() => {
        if (statIconDv.value) {
          const child = statIconDv.value.children[0] as HTMLElement;
          child.style.stroke = color.value;

          const div = statIconDv.value as HTMLElement;
          div.style.background = `rgba(${utilService.hexToRgb(
            color.value
          )}, 0.2)`;
        }
      }, 10);
    };

    const updateUserStats = (stats: any) => {
      user.value.stats = stats;
      mapValueForStat(props.stat);
      setStatsValue();
    };

    const handleClick = (data: any) => {
      if (props.fromPopup) context.emit("onClickItem", data);
      else if (props.userData?._id) handleStatsClick(data, props.userData._id);
    };

    onMounted(() => {
      mapValueForStat(props.stat);
      setStatsValue();
      checkOverflow();
      emitter.on("updateStatsItem", updateUserStats);
    });

    onBeforeUnmount(() => {
      emitter.off("updateStatsItem", updateUserStats);
    });

    watch(
      () => props,
      () => updateUserStats(user.value.stats)
    );

    const formatWithComma = (num: number | string) => {
      if (!num && num !== 0) return num;
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    return {
      formatWithComma,
      statIconDv,
      handleClick,
      value,
      icon,
      onRemove,
      color,
      isOverflowing,
      label,
      locale,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "./topstats";
</style>
